import React, { useState, useEffect } from 'react';
import { useNotify, useDataProvider } from 'react-admin';
import { 
    Typography, Select, MenuItem, FormControl, InputLabel, 
    Button, Box, OutlinedInput, Chip, Paper, Table, 
    TableBody, TableCell, TableContainer, TableHead, TableRow, Stack,
    TextField 
} from '@mui/material';

const PackageGroups = () => {
    const notify = useNotify();
    const dataProvider = useDataProvider();

    // States for Form
    const [packages, setPackages] = useState([]);
    const [groupName, setGroupName] = useState(""); 
    const [selectedSrvIds, setSelectedSrvIds] = useState([]); 
    
    // State for Update Logic
    const [editingId, setEditingId] = useState(null);

    // State for Display Table
    const [groupedList, setGroupedList] = useState([]);
    const [loading, setLoading] = useState(true);

    // 1. Fetch available packages for the dropdown using the NEW dataProvider logic
    const fetchPackages = async () => {
        try {
            const response = await dataProvider.getList('all-packages', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'srvname', order: 'ASC' },
                filter: {},
            });
            // The dataProvider already handles the mapping to .data
            setPackages(response.data || []);
        } catch (error) {
            console.error("Fetch Packages Error:", error);
            notify('Failed to fetch packages from the new API', { type: 'error' });
        }
    };

    // 2. Fetch existing groups
const fetchGroups = async () => {
    setLoading(true);
    try {
        const response = await dataProvider.getList('package-groups', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'group_name', order: 'ASC' },
            filter: {},
        });
        
        setGroupedList(response?.data ?? []);
    } catch (error) {
        console.error("Fetch Groups Error:", error);
    

        setGroupedList([]); 
                if (error?.status >= 500) {
            notify('Server error fetching groups', { type: 'error' });
        }
    } finally {
        setLoading(false);
    }
};
    useEffect(() => {
        fetchPackages();
        fetchGroups();
    }, []);

    // 3. Populate form for Editing
    const handleEditClick = (row) => {
        setEditingId(row.id);
        setGroupName(row.group_name);
        
        // Map the names in 'linked_packages' back to their srvids/package_ids
        const namesArray = row.linked_packages ? row.linked_packages.split(',').map(s => s.trim()) : [];
        const linkedIds = packages
            .filter(p => namesArray.includes(p.srvname || p.package_title))
            .map(p => p.srvid || p.package_id || p.id);
        
        setSelectedSrvIds(linkedIds);
        
        // Smooth scroll to the top form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // 4. Reset Form
    const resetForm = () => {
        setGroupName("");
        setSelectedSrvIds([]);
        setEditingId(null);
    };

    // 5. Handle Submit (Create or Update)
    const handleSendRequest = async () => {
        if (!groupName.trim() || selectedSrvIds.length === 0) {
            notify('Enter a group name and select at least one package', { type: 'warning' });
            return;
        }

        try {
            if (editingId) {
                // UPDATE logic
                await dataProvider.update('package-groups', {
                    id: editingId,
                    data: {
                        group_name: groupName,
                        srvids: selectedSrvIds,
                    }
                });
                notify('Group updated successfully');
            } else {
                // CREATE logic
                await dataProvider.create('package-groups', {
                    data: {
                        group_name: groupName,
                        srvids: selectedSrvIds,
                    }
                });
                notify('Group Created Successfully');
            }
            
            resetForm();
            fetchGroups(); 
        } catch (error) {
            notify(editingId ? 'Update failed' : 'Creation failed', { type: 'error' });
        }
    };

    // 6. Handle Delete
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete the group "${name}"?`)) {
            return;
        }

        try {
            await dataProvider.delete('package-groups', { id: id });
            notify('Group deleted successfully', { type: 'info' });
            if (editingId === id) resetForm();
            fetchGroups(); 
        } catch (error) {
            notify('Error deleting group', { type: 'error' });
        }
    };

    return (
        <Box p={3}>
            {/* INPUT SECTION */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: '8px', borderLeft: editingId ? '6px solid #ed6c02' : '6px solid #1976d2' }}>
                <Typography variant="h6" gutterBottom color={editingId ? "secondary" : "primary"} sx={{ fontWeight: 'bold' }}>
                    {editingId ? `Editing Group: ${groupName}` : "Create New Package Group"}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '500px' }}>
                    <TextField
                        label="Group Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                    />

                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Select Packages</InputLabel>
                        <Select
                            multiple
                            value={selectedSrvIds}
                            onChange={(e) => setSelectedSrvIds(e.target.value)}
                            input={<OutlinedInput label="Select Packages" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const pkg = packages.find(p => (p.srvid === value || p.package_id === value || p.id === value));
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={pkg ? (pkg.srvname || pkg.package_title) : `ID: ${value}`} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined" 
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {packages.map((pkg) => (
                                <MenuItem key={pkg.id || pkg.srvid || pkg.package_id} value={pkg.id || pkg.srvid || pkg.package_id}>
                                    {pkg.srvname || pkg.package_title} (ID: {pkg.srvid || pkg.package_id})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="contained" 
                            onClick={handleSendRequest}
                            color={editingId ? "warning" : "primary"}
                            sx={{ py: 1.2, fontWeight: 'bold', flexGrow: 1 }}
                        >
                            {editingId ? "Update Group" : "Save Group"}
                        </Button>
                        {editingId && (
                            <Button variant="outlined" color="inherit" onClick={resetForm}>
                                Cancel
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* DISPLAY SECTION */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Existing Package Groups
            </Typography>
            <TableContainer component={Paper} elevation={1}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Group Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Linked Package Titles</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">Loading groups...</TableCell>
                            </TableRow>
                        ) : groupedList.length > 0 ? (
                            groupedList.map((row) => (
                                <TableRow key={row.id} hover sx={{ backgroundColor: editingId === row.id ? '#fff9f2' : 'inherit' }}>
                                    <TableCell>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            {row.group_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {row.linked_packages ? row.linked_packages.split(',').map((name, idx) => (
                                                <Chip 
                                                    key={idx} 
                                                    label={name.trim()} 
                                                    size="small" 
                                                    sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 500, my: 0.5 }} 
                                                />
                                            )) : (
                                                <Typography variant="caption" color="textSecondary">No packages linked</Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Button 
                                                size="small" 
                                                onClick={() => handleEditClick(row)}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                Edit
                                            </Button>
                                            <Button 
                                                color="error" 
                                                size="small" 
                                                onClick={() => handleDelete(row.id, row.group_name)}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center">No groups found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PackageGroups;